import React, { createContext, useContext, useState, useEffect } from 'react';

const ThemeContext = createContext(null);

// public/index.html reads this same key in an inline script at the top of
// <body>, so the class is on before the first paint. Change both together.
export const STORAGE_KEY = 'assistAlienDarkMode';

// Applies/removes a single `dark-theme` class on <body>. Every dark-mode
// override in the app (darkTheme.css) is written as `.dark-theme <selector>`,
// so flipping this one class is all that's needed anywhere else — no
// component needs to read theme state itself unless it wants to (e.g. to
// swap an icon).
export const ThemeProvider = ({ children }) => {
  const [isDark, setIsDark] = useState(() => {
    let dark = false;
    try {
      dark = localStorage.getItem(STORAGE_KEY) === 'true';
    } catch {
      dark = false;
    }
    // Here as well as in the effect below: the effect runs after the first
    // render has painted. index.html's script normally got there first; this
    // covers a page where it did not run (2026-09-29).
    if (typeof document !== 'undefined') document.body.classList.toggle('dark-theme', dark);
    return dark;
  });

  useEffect(() => {
    document.body.classList.toggle('dark-theme', isDark);
    try {
      localStorage.setItem(STORAGE_KEY, String(isDark));
    } catch {
      // ignore storage errors (private browsing, etc.)
    }
  }, [isDark]);

  const toggleTheme = () => setIsDark(prev => !prev);

  return (
    <ThemeContext.Provider value={{ isDark, toggleTheme }}>
      {children}
    </ThemeContext.Provider>
  );
};

export const useTheme = () => {
  const ctx = useContext(ThemeContext);
  if (!ctx) throw new Error('useTheme must be used within a ThemeProvider');
  return ctx;
};
